import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PlayerIframe from '../components/PlayerIframe';
import { fetchEventDetails, getLiveStreamUrl } from '../services/api';
import { Event } from '../types';
import useMetaTags from '../hooks/useMetaTags';
import { RICH_SNIPPETS } from '../config/seo.config';

interface RouteParams {
    id: string;
}

const EventDetail: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();
    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [streamLoading, setStreamLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [streamUrl, setStreamUrl] = useState<string | null>(null);
    const [showPlayer, setShowPlayer] = useState<boolean>(false);

    // SEO Meta Tags
    useMetaTags({
        title: event ? `${event.name} - ${event.category === 'Cricket' ? 'ক্রিকেট' : 'ফুটবল'} লাইভ স্ট্রিমিং | BD Live Cric` : 'ইভেন্ট বিস্তারিত | BD Live Cric',
        description: event ? `${event.name} এর লাইভ স্ট্রিমিং এখানে দেখুন। ${event.teamA || ''} বনাম ${event.teamB || ''} ম্যাচ ${event.location} এ অনুষ্ঠিত। বাংলাদেশে সরাসরি লাইভ সম্প্রচার।` : 'ইভেন্টের বিস্তারিত দেখুন',
        keywords: event ? `${event.name}, ${event.category}, লাইভ স্ট্রিমিং, ${event.teamA}, ${event.teamB}, বাংলাদেশ খেলাধুলা` : '',
        url: `https://bdlink.live/event/${id}`,
        canonicalUrl: `https://bdlink.live/event/${id}`,
        image: 'https://bdlink.live/og-image.jpg',
        locale: 'bn_BD',
        robots: 'index, follow',
        structuredData: event ? RICH_SNIPPETS.sportEvent({
            name: event.name,
            description: `${event.name} ম্যাচ ${event.location} এ অনুষ্ঠিত হচ্ছে`,
            startDate: event.date,
            endDate: event.date,
            location: event.location,
            teamA: event.teamA,
            teamB: event.teamB
        }) : undefined
    });

    useEffect(() => {
        const loadEvent = async () => {
            try {
                setLoading(true);
                const data = await fetchEventDetails(id);
                if (data) {
                    setEvent(data);
                    setError(null);
                } else {
                    setError('ইভেন্ট পাওয়া যায়নি');
                }
            } catch (err) {
                setError('ইভেন্ট বিস্তারিত লোড করতে ব্যর্থ');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadEvent();
    }, [id]);

    // Handle play button click - fetch live stream URL
    const handlePlayClick = async () => {
        try {
            setStreamLoading(true);
            setError(null);
            
            const url = await getLiveStreamUrl(id);
            
            if (url) {
                setStreamUrl(url);
                setShowPlayer(true);
                setError(null);
            } else {
                // Stream not available - don't fall back to YouTube
                setError('⚠️ Live stream is not available at this moment. The event may not have started streaming yet, or the stream is temporarily unavailable. Please try again later.');
                setShowPlayer(false);
            }
        } catch (err) {
            setError('❌ Failed to load stream. Please try again later.');
            console.error(err);
        } finally {
            setStreamLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                minHeight: '100vh',
                backgroundColor: '#ecf0f1'
            }}>
                <p style={{ fontSize: '1.2em', color: '#34495e' }}>⏳ Loading event...</p>
            </div>
        );
    }

    if (error && !event) {
        return (
            <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                minHeight: '100vh',
                backgroundColor: '#ecf0f1'
            }}>
                <div style={{
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    maxWidth: '600px',
                    margin: '0 auto',
                    marginBottom: '20px'
                }}>
                    {error || 'Event not found'}
                </div>
                <button
                    onClick={() => history.push('/')}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '1em'
                    }}
                >
                    ← Back to Home
                </button>
            </div>
        );
    }

    if (!event) {
        return (
            <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                minHeight: '100vh',
                backgroundColor: '#ecf0f1'
            }}>
                <p style={{ fontSize: '1.2em', color: '#34495e' }}>Event not found</p>
            </div>
        );
    }

    const getStatusColor = (status?: string) => {
        const colors: { [key: string]: string } = {
            live: '#e74c3c',
            scheduled: '#3498db',
            completed: '#95a5a6',
        };
        return colors[status || ''] || '#34495e';
    };

    return (
        <div style={{ backgroundColor: '#ecf0f1', minHeight: '100vh', padding: '20px' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* Back Button */}
                <button
                    onClick={() => history.push('/')}
                    style={{
                        marginBottom: '20px',
                        padding: '8px 16px',
                        backgroundColor: '#34495e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                    }}
                >
                    ← Back to Home
                </button>

                {/* Player Container or Play Button */}
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    marginBottom: '30px'
                }}>
                    {showPlayer && streamUrl ? (
                        <PlayerIframe src={streamUrl} />
                    ) : (
                        <div style={{
                            position: 'relative',
                            paddingTop: '56.25%',
                            backgroundColor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.8)'
                            }}>
                                <button
                                    onClick={handlePlayClick}
                                    disabled={streamLoading}
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        backgroundColor: '#e74c3c',
                                        color: 'white',
                                        border: 'none',
                                        fontSize: '40px',
                                        cursor: streamLoading ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.3s ease',
                                        opacity: streamLoading ? 0.7 : 1
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!streamLoading) {
                                            e.currentTarget.style.backgroundColor = '#c0392b';
                                            e.currentTarget.style.transform = 'scale(1.1)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#e74c3c';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                >
                                    {streamLoading ? '⏳' : '▶'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        padding: '15px',
                        borderRadius: '4px',
                        marginBottom: '20px'
                    }}>
                        {error}
                    </div>
                )}

                {/* Event Info */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr',
                    gap: '20px'
                }}>
                    {/* Main Info */}
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '30px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                            <h1 style={{ margin: 0, color: '#2c3e50', flex: 1 }}>{event.name}</h1>
                            <span style={{
                                backgroundColor: getStatusColor(event.status),
                                color: 'white',
                                padding: '8px 16px',
                                borderRadius: '4px',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                fontSize: '0.9em'
                            }}>
                                {event.status || 'N/A'}
                            </span>
                        </div>

                        <div style={{
                            backgroundColor: '#f8f9fa',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '20px'
                        }}>
                            <p style={{ margin: '10px 0', fontSize: '1.1em' }}>
                                <strong>📅 Date:</strong> {event.date}
                            </p>
                            <p style={{ margin: '10px 0', fontSize: '1.1em' }}>
                                <strong>🕐 Time:</strong> {event.time}
                            </p>
                            <p style={{ margin: '10px 0', fontSize: '1.1em' }}>
                                <strong>📍 Location:</strong> {event.location}
                            </p>
                            {event.category && (
                                <p style={{ margin: '10px 0', fontSize: '1.1em' }}>
                                    <strong>🏆 Category:</strong> {event.category}
                                </p>
                            )}
                        </div>

                        <div style={{
                            backgroundColor: '#e8f4f8',
                            border: '1px solid #3498db',
                            borderRadius: '8px',
                            padding: '20px',
                            marginTop: '20px'
                        }}>
                            <p style={{ margin: 0, color: '#2c3e50', fontSize: '1em' }}>
                                ℹ️ <strong>Live Stream Information</strong>
                            </p>
                            <p style={{
                                margin: '10px 0 0 0',
                                color: '#7f8c8d',
                                fontSize: '0.95em'
                            }}>
                                {streamLoading ? 'Loading stream...' : streamUrl ? 'Stream loaded successfully!' : 'Click the play button to start the stream'}
                            </p>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                            <h3 style={{ margin: '0 0 15px 0', color: '#2c3e50' }}>Stream Details</h3>
                            
                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '15px',
                                borderRadius: '8px',
                                marginBottom: '15px'
                            }}>
                                <p style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#7f8c8d' }}>
                                    STATUS
                                </p>
                                <p style={{
                                    margin: 0,
                                    fontSize: '1.2em',
                                    fontWeight: 'bold',
                                    color: getStatusColor(event.status)
                                }}>
                                    {event.status?.toUpperCase() || 'N/A'}
                                </p>
                            </div>

                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '15px',
                                borderRadius: '8px',
                                marginBottom: '15px'
                            }}>
                                <p style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#7f8c8d' }}>
                                    STREAM STATUS
                                </p>
                                <p style={{ margin: 0, fontSize: '1.1em', fontWeight: 'bold' }}>
                                    {streamUrl ? '✅ Active' : '⏹️ Inactive'}
                                </p>
                            </div>

                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '15px',
                                borderRadius: '8px'
                            }}>
                                <p style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#7f8c8d' }}>
                                    EVENT ID
                                </p>
                                <p style={{ margin: 0, fontSize: '0.85em', fontWeight: 'bold', wordBreak: 'break-all' }}>
                                    {id}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventDetail;